/**
 * CRM Activities Query Hooks
 *
 * TanStack Query hooks for activity management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getOpportunityActivities,
  createActivity,
  updateActivity,
  completeActivity,
  deleteActivity,
  getUpcomingActivities,
} from '../api/crmApi';
import { crmPipelineKeys } from './useCrmPipeline';
import { CACHE_STALE_TIMES } from '../constants';
import type { CreateCrmActivityInput, UpdateCrmActivityInput, CrmActivityOutcome } from '../types';
import { getErrorMessage } from '../utils/getErrorMessage';

// =============================================================================
// Query Key Factory
// =============================================================================

export const crmActivityKeys = {
  all: ['crm-activities'] as const,
  // When filters are omitted (e.g. from mutation invalidations) the key is left
  // at the opportunity level so it prefix-matches every filtered query for that
  // opportunity; otherwise the trailing `undefined` would differ from a live
  // query's filter object and the feed would not refetch after a mutation.
  byOpportunity: (accountId: string, opportunityId: string, filters?: object) =>
    filters
      ? ([...crmActivityKeys.all, 'opportunity', accountId, opportunityId, filters] as const)
      : ([...crmActivityKeys.all, 'opportunity', accountId, opportunityId] as const),
  byAccount: (accountId: string, filters?: object) =>
    [...crmActivityKeys.all, 'account', accountId, filters] as const,
  upcoming: (params?: object) => [...crmActivityKeys.all, 'upcoming', params] as const,
  single: (accountId: string, opportunityId: string, activityId: string) =>
    [...crmActivityKeys.all, accountId, opportunityId, activityId] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch activities for an opportunity
 */
export function useOpportunityActivities(
  accountId: string,
  opportunityId: string,
  params?: {
    status?: string;
    type?: string;
    page_number?: number;
    page_length?: number;
  }
) {
  return useQuery({
    queryKey: crmActivityKeys.byOpportunity(accountId, opportunityId, params),
    queryFn: () => getOpportunityActivities(accountId, opportunityId, params),
    enabled: !!accountId && !!opportunityId,
    staleTime: CACHE_STALE_TIMES.ACTIVITIES,
  });
}

/**
 * Hook to fetch upcoming activities (for dashboard widget)
 */
export function useUpcomingActivities(params?: {
  account_id?: string;
  days?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: crmActivityKeys.upcoming(params),
    queryFn: () => getUpcomingActivities(params),
    staleTime: CACHE_STALE_TIMES.UPCOMING_ACTIVITIES,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to create a new activity
 */
export function useCreateActivity(accountId: string, opportunityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCrmActivityInput) =>
      createActivity(accountId, opportunityId, input),
    onSuccess: (result) => {
      // Invalidate activities for this opportunity
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.byOpportunity(accountId, opportunityId),
      });
      // Also invalidate upcoming activities
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.upcoming(),
      });
      // Invalidate pipeline to update activity counts
      queryClient.invalidateQueries({
        queryKey: crmPipelineKeys.all,
      });
      toast.success('Activity created', { description: result.title });
    },
    onError: (error) => {
      toast.error('Failed to create activity', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update an activity
 */
export function useUpdateActivity(accountId: string, opportunityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ activityId, input }: { activityId: string; input: UpdateCrmActivityInput }) =>
      updateActivity(accountId, opportunityId, activityId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.byOpportunity(accountId, opportunityId),
      });
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.upcoming(),
      });
      toast.success('Activity updated');
    },
    onError: (error) => {
      toast.error('Failed to update activity', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to mark an activity as completed
 */
export function useCompleteActivity(accountId: string, opportunityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      activityId,
      outcome,
      outcomeNotes,
    }: {
      activityId: string;
      outcome?: CrmActivityOutcome;
      outcomeNotes?: string;
    }) => completeActivity(accountId, opportunityId, activityId, outcome ?? undefined, outcomeNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.byOpportunity(accountId, opportunityId),
      });
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.upcoming(),
      });
      // Invalidate pipeline to update activity counts
      queryClient.invalidateQueries({
        queryKey: crmPipelineKeys.all,
      });
      toast.success('Activity completed');
    },
    onError: (error) => {
      toast.error('Failed to complete activity', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete an activity
 */
export function useDeleteActivity(accountId: string, opportunityId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: string) => deleteActivity(accountId, opportunityId, activityId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.byOpportunity(accountId, opportunityId),
      });
      queryClient.invalidateQueries({
        queryKey: crmActivityKeys.upcoming(),
      });
      queryClient.invalidateQueries({
        queryKey: crmPipelineKeys.all,
      });
      toast.success('Activity deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete activity', { description: getErrorMessage(error) });
    },
  });
}
